import React, { useEffect, useState } from 'react';

import Accordion from '@components/ui/Accordion/next/Accordion';
import {
  HotelDetailFeatureCategoryViewModel,
  HotelFeatureSeasonViewModel,
} from '@api/index';
import {
  Available,
  AvailableLimited,
  SummerIcon,
  WinterIcon,
} from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { formatArrayToString } from '@utils/utils';
import Markdown from '@components/utils/Markdown';
import { useBreakpoint } from '@contexts/breakpoints';
import { Typography } from '@components/Typography';

import {
  AccordionContainer,
  BodyContainer,
  ConditionWrapper,
  Container,
  Feature,
  FeatureListContainer,
  NonFreeWrapper,
  StyledTab,
  TabsContainer,
  TabText,
  TabWrapper,
  TitleCondition,
} from './styled';
import { isBetween } from './isBetween';

export const HOTEL_SEASON_FEATURES = {
  0: 'Summer',
  1: 'Winter',
};

const HotelFeatures = ({
  hotelFeatures,
  beginDate,
  generalConditions,
}: {
  hotelFeatures: HotelFeatureSeasonViewModel[];
  features?: Array<HotelDetailFeatureCategoryViewModel>;
  beginDate?: Date;
  generalConditions?: string;
}) => {
  const [selected, setSelected] = useState(0);

  const summerFeature = hotelFeatures.some(
    (feature) => feature.name === 'Summer'
  );
  const winterFeature = hotelFeatures.some(
    (feature) => feature.name === 'Winter'
  );
  const hasSeasonFeatures = summerFeature || winterFeature;

  const onTabSelect = (s: number) => {
    setSelected(s);
  };

  const winter = hotelFeatures.find((item) => item.name === 'Winter');

  const winterStart = new Date(formatArrayToString(winter?.startDate || ''));
  const winterEnd = new Date(formatArrayToString(winter?.endDate || ''));

  const sessionWinter = isBetween({
    beginDate: beginDate as Date,
    start: winterStart,
    end: winterEnd,
  });

  useEffect(() => {
    if (sessionWinter && winterFeature) {
      setSelected(1);
    }
  }, [sessionWinter, winterFeature]);

  const selectedSeasonFeatures = hotelFeatures.find(
    (item) => item.name === HOTEL_SEASON_FEATURES[selected]
  );

  const isSummerSelected = selected === 0;
  const isWinterSelected = selected === 1;

  const { xl: isDesktop } = useBreakpoint();

  return (
    <Container>
      {hasSeasonFeatures ? (
        <>
          <TabsContainer
            style={{
              width: hasSeasonFeatures ? '100%' : isDesktop ? '50%' : '100%',
            }}
          >
            <TabWrapper>
              {summerFeature && (
                <StyledTab
                  onClick={() => onTabSelect(0)}
                  selected={isSummerSelected}
                >
                  <CustomIcon
                    paletteColor={isSummerSelected ? 'white' : 'primaryBrand'}
                    fontSize={isDesktop ? 24 : 18}
                    width={isDesktop ? 24 : 18}
                    height={isDesktop ? 24 : 18}
                    icon={SummerIcon}
                  />
                  <TabText isSelected={isSummerSelected}>Yaz Sezonu</TabText>
                </StyledTab>
              )}

              {winterFeature && (
                <StyledTab
                  onClick={() => onTabSelect(1)}
                  selected={isWinterSelected}
                >
                  <CustomIcon
                    paletteColor={isWinterSelected ? 'white' : 'primaryBrand'}
                    fontSize={isDesktop ? 24 : 18}
                    width={isDesktop ? 24 : 18}
                    height={isDesktop ? 24 : 18}
                    icon={WinterIcon}
                  />
                  <TabText isSelected={isWinterSelected}>Kış Sezonu</TabText>
                </StyledTab>
              )}
            </TabWrapper>
          </TabsContainer>
          <AccordionContainer>
            {selectedSeasonFeatures?.categories?.map((featureGroup) => (
              <Accordion
                key={featureGroup.id}
                borderRadius='8px'
                headerFullBorder={true}
                showBottomBorderOnOpened={false}
                alwaysShowBottomBorder={false}
                showBottomBorderOnClosed={true}
                showBodyBorder={true}
                headerPadding='16px'
                iconColor='darkGrey'
                iconSize={20}
                contentPadding='0px'
                label={
                  <Typography
                    variant='Title5'
                    color='darkGrey'
                    weight='SemiBold'
                  >
                    {featureGroup.name}
                  </Typography>
                }
              >
                <BodyContainer>
                  <FeatureListContainer>
                    {featureGroup.features?.map((feat) => (
                      <Feature key={feat.name}>
                        {featureGroup.id === 21 ||
                        featureGroup.id === 17 ? null : (
                          <CustomIcon
                            icon={feat.charged ? AvailableLimited : Available}
                            fontSize={20}
                          />
                        )}

                        <Typography
                          variant='caption2'
                          color='black11'
                          weight='Regular'
                        >
                          {feat.name}
                        </Typography>
                      </Feature>
                    ))}
                  </FeatureListContainer>
                  {featureGroup.features.some((feature) => feature.charged) ? (
                    <NonFreeWrapper>
                      <Typography
                        variant='caption2'
                        color='black11'
                        weight='Regular'
                      >
                        ₺ 'ile belirtilen özellikler ücretlidir.
                      </Typography>
                    </NonFreeWrapper>
                  ) : null}
                </BodyContainer>
              </Accordion>
            ))}
          </AccordionContainer>
        </>
      ) : (
        <>
          {hotelFeatures?.map((item) =>
            item.name === 'NoSeason' ? (
              <>
                {item?.categories?.map((featureGroup) => (
                  <Accordion
                    key={featureGroup.id}
                    backgroundColor='white'
                    bodyBackgroundColor='white'
                    borderRadius='8px'
                    borderColor='lightBlueGrey'
                    headerFullBorder={true}
                    showBottomBorderOnOpened={false}
                    alwaysShowBottomBorder={false}
                    showBottomBorderOnClosed={true}
                    showBodyBorder={true}
                    headerPadding='16px'
                    headerContentColor='darkGrey'
                    iconColor='darkGrey'
                    iconSize={20}
                    contentPadding='0px'
                    label={
                      <Typography
                        variant='Title5'
                        color='darkGrey'
                        weight='SemiBold'
                      >
                        {featureGroup.name}
                      </Typography>
                    }
                  >
                    <BodyContainer>
                      <FeatureListContainer>
                        {featureGroup?.features?.map((feat) => (
                          <Feature key={feat.name}>
                            {featureGroup.id === 21 ||
                            featureGroup.id === 17 ? null : (
                              <CustomIcon
                                icon={
                                  feat.charged ? AvailableLimited : Available
                                }
                                fontSize={20}
                              />
                            )}

                            <Typography
                              variant='caption2'
                              color='black'
                              weight='Regular'
                            >
                              {feat.name}
                            </Typography>
                          </Feature>
                        ))}
                      </FeatureListContainer>
                      {featureGroup.features.some(
                        (feature) => feature.charged
                      ) ? (
                        <NonFreeWrapper>
                          <Typography
                            variant='caption2'
                            color='black11'
                            weight='Regular'
                          >
                            ₺ 'ile belirtilen özellikler ücretlidir.
                          </Typography>
                        </NonFreeWrapper>
                      ) : null}
                    </BodyContainer>
                  </Accordion>
                ))}
              </>
            ) : null
          )}
        </>
      )}
      {generalConditions ? (
        <ConditionWrapper>
          <TitleCondition>
            <Typography
              variant={isDesktop ? 'Title4' : 'caption'}
              color='darkGrey'
              weight='Bold'
            >
              Genel Şartlar
            </Typography>
          </TitleCondition>
          <Markdown text={generalConditions || ''} />
        </ConditionWrapper>
      ) : null}
    </Container>
  );
};

export default HotelFeatures;
